/**
 * The single card primitive for every app (BL-1244, ratifying the `Card` row
 * of the ADR 030 Canon).
 *
 * The merge collapses two implementations of the same job — bidder-web's
 * `Card` (the canonical base per ADR 030 § 3b: a compound component with
 * `hover`/`muted` variants and HTML attribute passthrough) and admin-web's
 * `Card` (a single div with a padding preset). Both drew the same thing: a
 * white box with a radius, a hairline border and a subtle shadow. They
 * differed only in VALUES — corner radius, border shade, whether padding sat
 * on the root or in a body section, and whether a hover lift existed.
 *
 * WHY a `dialect` object rather than a pile of `className` props: ADR 030's
 * foundation is shared but its dialects are deliberately different (admin's
 * 8px/`gray-200` back-office chrome, the buyer site's 12px/`zinc-200`), and
 * `sharing.md` § 1 caps a per-app skin at a 2–5 line wrapper. One frozen
 * object of class names per app keeps the appearance a piece of DATA the skin
 * declares once, so the wrapper stays a passthrough instead of re-deriving
 * styling on every render. Unifying the two dialects is a regression, not a
 * cleanup — see ADR 030 § 3b.
 *
 * WHY the sections are separate exports rather than props: a header/body/
 * footer passed as `ReactNode` props forces the caller to hand over content
 * with no control over ordering or repetition, and bidder's account pages
 * compose several bodies under one header. Separate parts keep composition in
 * the caller's hands, which is the same reasoning the `Modal` ruling records.
 *
 * WHY padding is NOT a core concern: bidder's root carries no padding (each
 * section owns its own inset) while admin's root carries a preset. Rather than
 * teaching the core two layout models, admin's skin maps its preset to a class
 * and passes it through `className`. The core stays uniform.
 */
'use client';

import clsx from 'clsx';
import type { HTMLAttributes, ReactNode } from 'react';

/**
 * Per-app appearance binding — class names only, no structure.
 *
 * Every slot is optional: a consumer with no dialect gets bare structure,
 * which is the right default for a third app that hasn't declared one yet.
 */
export interface CardDialect {
    /** The outer box — radius, border, background, shadow, clipping. */
    root?: string;
    /**
     * Applied to the root only when `hover` is set — e.g. a shadow lift.
     *
     * Kept separate from `root` on purpose. A hover utility left in `root`
     * would apply unconditionally; a state-dependent value only reads
     * correctly when the variant that gates it is the thing that emits it.
     */
    rootHover?: string;
    /** The header section — layout and its dividing border. */
    header?: string;
    /**
     * Applied to the header only when `muted` is set — the tinted background.
     *
     * Do NOT fold this into `header`. Both would land on the element at equal
     * CSS specificity and the later-emitted utility would win the cascade
     * regardless of the order they appear in the class attribute — the exact
     * collision that bit the BL-1243 admin radio dialect.
     */
    headerMuted?: string;
    /** The body section — its inset. */
    body?: string;
    /** The footer section — layout and its dividing border. */
    footer?: string;
    /** Applied to the footer only when `muted` is set. See {@link CardDialect.headerMuted}. */
    footerMuted?: string;
}

/**
 * Props for {@link Card}.
 *
 * Extends `HTMLAttributes<HTMLDivElement>` so a call site can attach
 * `onClick`, `id`, `role`, `aria-*` and friends directly — bidder's browse
 * grid and carousel both rely on this.
 */
export interface CardProps extends HTMLAttributes<HTMLDivElement> {
    /** Opts into the dialect's hover treatment. */
    hover?: boolean;
    /** Per-app appearance binding. See {@link CardDialect}. */
    dialect?: CardDialect;
    children: ReactNode;
}

/**
 * The card container. Compose {@link CardHeader}, {@link CardBody} and
 * {@link CardFooter} inside it, or pass plain children for a simple box.
 *
 * @param props - {@link CardProps}
 * @returns The rendered card.
 */
export function Card({ hover, dialect, className, children, ...props }: CardProps) {
    return (
        <div
            // `className` last so a call site can still override the dialect.
            className={clsx(dialect?.root, hover && dialect?.rootHover, className)}
            {...props}
        >
            {children}
        </div>
    );
}

/**
 * Props for {@link CardHeader} and {@link CardFooter}.
 */
export interface CardSectionProps extends HTMLAttributes<HTMLDivElement> {
    /** Opts into the dialect's tinted background. */
    muted?: boolean;
    /** Per-app appearance binding. See {@link CardDialect}. */
    dialect?: CardDialect;
    children: ReactNode;
}

/**
 * The card's top section — typically a title on the left and actions on the
 * right, above a dividing border.
 *
 * @param props - {@link CardSectionProps}
 * @returns The rendered header.
 */
export function CardHeader({ muted, dialect, className, children, ...props }: CardSectionProps) {
    return (
        <div className={clsx(dialect?.header, muted && dialect?.headerMuted, className)} {...props}>
            {children}
        </div>
    );
}

/**
 * Props for {@link CardBody}.
 */
export interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {
    /** Per-app appearance binding. See {@link CardDialect}. */
    dialect?: CardDialect;
    children: ReactNode;
}

/**
 * The card's main content section.
 *
 * @param props - {@link CardBodyProps}
 * @returns The rendered body.
 */
export function CardBody({ dialect, className, children, ...props }: CardBodyProps) {
    return (
        <div className={clsx(dialect?.body, className)} {...props}>
            {children}
        </div>
    );
}

/**
 * The card's bottom section — typically a summary row below a dividing border.
 *
 * @param props - {@link CardSectionProps}
 * @returns The rendered footer.
 */
export function CardFooter({ muted, dialect, className, children, ...props }: CardSectionProps) {
    return (
        <div className={clsx(dialect?.footer, muted && dialect?.footerMuted, className)} {...props}>
            {children}
        </div>
    );
}
